import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import trackService from '../services/trackService';
import type { Track, Course } from '../services/trackService';
import axios from 'axios';
import AddCoursesForm from '../components/Tracks/AddCoursesForm';

/**
 * Page for adding courses to a track
 */
const AddCoursesToTrack: React.FC = () => {
  const { trackId } = useParams<{ trackId: string }>();
  const navigate = useNavigate();
  
  const [track, setTrack] = useState<Track | null>(null);
  const [trackCourses, setTrackCourses] = useState<Course[]>([]);
  const [allCourses, setAllCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!trackId) {
        setError('Track ID is missing');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch track details
        const trackData = await trackService.getTrack(trackId);
        setTrack(trackData);
        
        // Fetch courses for this track
        const trackCoursesData = await trackService.getTrackCourses(trackId);
        setTrackCourses(trackCoursesData);
        
        // Fetch all available courses
        const allCoursesData = await trackService.getAllCourses();
        setAllCourses(allCoursesData);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        if (axios.isAxiosError(err) && err.response) {
          setError(`Failed to load data: ${err.response.status} ${err.response.statusText}. ${err.response.data?.error || ''}`);
        } else {
          setError('Failed to load data. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [trackId]);

  const handleBack = () => {
    navigate(`/tracks/${trackId}`);
  };

  const handleAddCourses = async (courseIds: string[]) => {
    if (!trackId || courseIds.length === 0) return;
    
    try {
      await trackService.addCoursesToTrack(trackId, courseIds);
      
      // Navigate back to track details page
      navigate(`/tracks/${trackId}`);
    } catch (error) {
      console.error('Error adding courses:', error);
      throw error;
    }
  };

  return (
    <div className="page-container">
      <div className="flex items-center mb-md">
        <button 
          className="btn btn-text"
          onClick={handleBack}
          style={{ marginRight: '16px' }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back to Track Details
        </button>
        <h1 className="page-title">Add Courses to Track</h1>
      </div>
      
      {loading ? (
        <div className="card">
          <div className="flex flex-col items-center justify-center p-lg">
            <div className="spinner"></div>
            <p className="mt-md">Loading courses...</p>
          </div>
        </div>
      ) : error ? (
        <div className="card">
          <div className="bg-error p-md" style={{ borderRadius: 'var(--border-radius-md)' }}>
            <h2 className="text-white mb-sm" style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'bold' }}>Error!</h2>
            <p className="text-white">{error}</p>
          </div>
        </div>
      ) : track ? (
        <div className="card">
          <div className="mb-lg">
            <h2 className="text-lg font-bold mb-sm">Track: {track.sku}</h2>
            <p>{track.description}</p>
          </div>
          
          <AddCoursesForm 
            trackId={trackId || ''}
            existingCourses={trackCourses}
            availableCourses={allCourses}
            onSubmit={handleAddCourses}
            onCancel={handleBack}
          />
        </div>
      ) : (
        <div className="card">
          <p className="text-center p-md">Track not found</p>
        </div>
      )}
    </div>
  );
};

export default AddCoursesToTrack;
